import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { useHistory, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import {
  JsonParam,
  NumberParam,
  StringParam,
  ArrayParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import {
  Button,
  Dropdown,
  SwitchTabs,
  useGlobalState,
  usePaginatedProjects,
} from '@pepper/ui'

import DownloadDropdown from './DownloadDropdown/DownloadDropdown'
import AIDocMenu from 'components/CreateMenu/AIDocMenu'
import Sort from './Sort/Sort'
import { sortOptions } from './schema'
import Filters from './Filters/Filters'
import { useOrderItems } from 'utils/hooks/useOrderItems'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { useDocItems } from 'utils/hooks/useDocItems'

import s from './Header.module.css'

type Props<T> = {
  tabName: string
  viewType: 'project-items' | 'all-items'
  onDownload: () => void
  setSelectedItems: React.Dispatch<React.SetStateAction<T[]>>
  onOrderClick?: () => void
  handleDocsDownload?: () => void
  recordType?: 'draft' | 'items'
  onBack?: () => void
  project?: Project
  isEmptyItems?: boolean
  showFilter?: boolean
  isApprovedDocsListEmpty?: boolean
}

const Header = <T,>({
  viewType,
  onBack,
  project,
  onDownload,
  setSelectedItems,
  handleDocsDownload,
  tabName,
  recordType = 'items',
  isEmptyItems,
  onOrderClick,
  showFilter = true,
  isApprovedDocsListEmpty = false,
}: Props<T>): React.ReactElement => {
  const [isCreateDocOpen, setIsCreateDocOpen] = useState(false)
  const [queryParamFilters, setQueryParamFilters] = useQueryParams({
    search_title: withDefault(StringParam, undefined),
    item_types: withDefault(StringParam, undefined),
    stage: withDefault(ArrayParam, undefined),
    rework: withDefault(StringParam, undefined),
    vertical: withDefault(ArrayParam, undefined),
    sort: withDefault(JsonParam, {
      sort_by: sortOptions[0].sortBy,
      sort_order: sortOptions[0].sortOrder,
    }),
    status: withDefault(ArrayParam, []),
    assignee: withDefault(ArrayParam, []),
    currentPage: withDefault(NumberParam, 0),
    tab: withDefault(StringParam, undefined),
  })
  const companyId = useSelectedCompanyId()
  const { item_types } = queryParamFilters
  const history = useHistory()
  const location = useLocation()
  const isDraftHeader = recordType === 'draft'
  const disableMarketplaceFilters = item_types === 'private_doc'
  const { permissions } = useGlobalState()
  const { totalProjects } = usePaginatedProjects({
    company_id: companyId,
    page_size: 1,
  })

  const isMarketplaceEnabled = permissions?.marketplace

  const { total: totalOrders } = useOrderItems({
    companyId,
    pageSize: 1,
  })

  const { total: totalDocs } = useDocItems({
    companyId,
    pageSize: 1,
  })

  const clearAllFilters = () => {
    setQueryParamFilters({
      currentPage: 0,
      sort: {
        sort_by: sortOptions[0].sortBy,
        sort_order: sortOptions[0].sortOrder,
      },
      stage: undefined,
      rework: undefined,
      vertical: undefined,
      assignee: undefined,
      status: undefined,
    })
  }

  const aiDocMenuOverlay = permissions?.cmp ? (
    <AIDocMenu
      projectId={project?.id}
      onClose={() => setIsCreateDocOpen(false)}
    />
  ) : (
    <></>
  )

  const searchObj = new URLSearchParams(location.search)

  const getItemType = (tab: 'orders' | 'docs') => {
    return `?item_types=${
      tab === 'docs' ? 'private_doc' : 'assignment'
    }&limit=${searchObj.get('limit')}`
  }

  const getTabList = () => {
    const tabList = [
      {
        title: `Projects (${totalProjects})`,
        value: 'project',
        data_id: 'projectView_projects',
      },
      {
        title: `Internal Docs (${totalDocs})`,
        value: 'docs',
        data_id: 'projectView_items',
      },
    ]
    if (!!isMarketplaceEnabled || !!totalOrders) {
      tabList.splice(1, 0, {
        title: `Orders (${totalOrders})`,
        value: 'orders',
        data_id: 'projectView_items',
      })
    }
    return tabList
  }

  useEffect(() => {
    setSelectedItems([])
  }, [item_types, setSelectedItems])

  return (
    <div className={s.wrapper}>
      <div className={s.ctaOptions}>
        <div className={s.left}>
          <div
            className={clsx(s.container, {
              [s.emptyContainer]:
                !isDraftHeader && viewType === 'project-items',
            })}
          >
            {isDraftHeader && (
              <>
                <Button
                  type="link"
                  onClick={() => {
                    clearAllFilters()
                    onBack?.()
                  }}
                >
                  <Icon icon="octicon:arrow-left-16" /> Back to Items
                </Button>
              </>
            )}
            {!isDraftHeader && (
              <>
                {viewType === 'all-items' && (
                  <SwitchTabs
                    activeTab={tabName}
                    onTabChange={(tab: string) => {
                      const pathname =
                        tab === 'orders' || tab === 'docs'
                          ? '/projects/items'
                          : '/projects'
                      const search =
                        tab === 'orders' || tab === 'docs'
                          ? getItemType(tab)
                          : `?limit=${searchObj.get('limit')}`
                      history.push({ pathname, search })
                    }}
                    tabList={getTabList()}
                  />
                )}
              </>
            )}
          </div>
          {showFilter && (
            <div className={s.filterWrapper}>
              <div className={s.filter}>
                <Filters
                  disableMarketplaceFilters={disableMarketplaceFilters}
                  isEmptyItems={isEmptyItems}
                  queryParamFilters={queryParamFilters}
                  clearAllFilters={clearAllFilters}
                />
              </div>
              <div className={s.right}>
                {!isDraftHeader && (
                  <DownloadDropdown
                    handleCSVExport={onDownload}
                    handleDocsDownload={handleDocsDownload}
                    disableBulkDownload={isApprovedDocsListEmpty}
                  />
                )}
                <div className={s.rightContainer}>
                  {!disableMarketplaceFilters &&
                    viewType === 'project-items' && (
                      <Sort
                        queryParamFilters={queryParamFilters}
                        setQueryParamFilters={setQueryParamFilters}
                      />
                    )}
                  {!disableMarketplaceFilters && viewType === 'all-items' && (
                    <Sort
                      queryParamFilters={queryParamFilters}
                      setQueryParamFilters={setQueryParamFilters}
                    />
                  )}
                  {!isDraftHeader &&
                    (queryParamFilters.item_types === 'private_doc' ? (
                      <Dropdown
                        trigger={['click']}
                        placement="bottomLeft"
                        overlay={aiDocMenuOverlay}
                        visible={isCreateDocOpen}
                        onVisibleChange={setIsCreateDocOpen}
                      >
                        <Button
                          size="middle"
                          type="primary"
                          disabled={viewType === 'project-items'}
                        >
                          <Icon icon="memory:plus" width={16} height={16} />
                          Create New
                        </Button>
                      </Dropdown>
                    ) : (
                      <Button
                        size="middle"
                        type="primary"
                        onClick={onOrderClick}
                      >
                        <Icon icon="memory:plus" width={16} height={16} />
                        Order Content
                      </Button>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Header
